import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { IonPage, IonContent } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { applyNativeStatusBar } from '@shared/native/statusBar';

import './citizen-theme.css';
import { DarkModeProvider, useDarkMode } from './hooks/useDarkMode';
import { usePushNotifications } from '../hooks/useProPushNotifications';
import { getCitizenNotificationLink } from './lib/notificationLinks';
import { TabBar, TAB_PATHS } from './components/TabBar';
import HomeScreen from './screens/HomeScreen';
import BillScreen from './screens/BillScreen';
import CandidateScreen from './screens/CandidateScreen';
import LegislatorScreen from './screens/LegislatorScreen';
import CandidateFinanceScreen from './screens/CandidateFinanceScreen';
import BallotScreen from './screens/BallotScreen';
import ChatScreen from './screens/ChatScreen';
import BriefingScreen from './screens/BriefingScreen';
import RequestsListScreen from './screens/RequestsListScreen';
import RequestDetailScreen from './screens/RequestDetailScreen';
import ContactRepsScreen from './screens/ContactRepsScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import ProfileScreen from './screens/ProfileScreen';
import IntakePickerScreen from './screens/intake/IntakePickerScreen';
import IntakeQuickScreen from './screens/intake/IntakeQuickScreen';
import IntakeConvoScreen from './screens/intake/IntakeConvoScreen';
import IntakeSuccessScreen from './screens/intake/IntakeSuccessScreen';

/**
 * CitizenApp — the authenticated citizen experience, ported from the v2
 * standalone app. Mounted at `/citizen/*` inside the unified web app behind
 * CitizenRouteGuard, so all routes here assume an authenticated citizen.
 *
 * Routes are relative to the `/citizen` mount point; navigation throughout the
 * citizen screens uses absolute `/citizen/...` paths. Public citizen entry
 * (landing / login / signup) is wired separately in the app router.
 */
export default function CitizenApp() {
  return (
    <DarkModeProvider>
      <CitizenAppShell />
    </DarkModeProvider>
  );
}

function CitizenAppShell() {
  const location = useLocation();
  const { dark } = useDarkMode();
  const showTabBar = TAB_PATHS.includes(location.pathname);

  // Native push: register the device for FCM and route taps into /citizen/*.
  // No-op on web; permission is only prompted after a fresh native sign-in
  // (handleLoginSuccess sets the post-login flag for citizens too).
  usePushNotifications(true, {
    fallbackRoute: '/citizen/notifications',
    resolveLink: getCitizenNotificationLink,
  });

  // Citizen-aware native status bar. Citizen surfaces sit on a light cream
  // background (dark icons) in light mode, and a deep background (light icons)
  // in dark mode — distinct from the Pro purple status bar.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const apply = async () => {
      if (dark) {
        await applyNativeStatusBar({ iconStyle: 'light', backgroundColor: '#161219' });
      } else {
        await applyNativeStatusBar({ iconStyle: 'dark', backgroundColor: '#FAF8F4' });
      }
    };
    apply();
  }, [dark]);

  return (
    // .citizen-root scopes the design tokens + --ion-* bridge to the whole
    // citizen subtree (incl. IonContent's children). The centered relative
    // column gives IonPage (position:absolute) explicit dimensions to fill,
    // capped to the mobile width; IonicStandaloneView (in App.tsx) provides the
    // fixed viewport box this sits in. Mirrors the proven ProTabLayout chain.
    <div
      className="citizen-root"
      style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', background: 'var(--background)' }}
    >
      <div style={{ position: 'relative', width: '100%', maxWidth: '28rem', height: '100%' }}>
        <IonPage>
          <IonContent
            style={
              {
                '--background': 'var(--background)',
                '--padding-top': 'var(--ion-safe-area-top, env(safe-area-inset-top, 0px))',
                '--padding-bottom': showTabBar
                  ? 'calc(56px + max(env(safe-area-inset-bottom), 20px))'
                  : '0px',
              } as React.CSSProperties
            }
          >
            <Routes>
              {/* Tab screens */}
              <Route path="home" element={<HomeScreen />} />
              <Route path="ballot" element={<BallotScreen />} />
              <Route path="chat" element={<ChatScreen />} />
              <Route path="profile" element={<ProfileScreen />} />

              {/* Detail screens */}
              <Route path="bill/:id" element={<BillScreen />} />
              <Route path="legislator/:id" element={<LegislatorScreen />} />
              <Route path="candidate/:id" element={<CandidateScreen />} />
              <Route path="candidate/:id/finance" element={<CandidateFinanceScreen />} />
              <Route path="briefing" element={<BriefingScreen />} />
              <Route path="requests" element={<RequestsListScreen />} />
              <Route path="requests/:id" element={<RequestDetailScreen />} />
              <Route path="calls" element={<ContactRepsScreen />} />
              <Route path="notifications" element={<NotificationsScreen />} />

              {/* Intake / onboarding */}
              <Route path="intake" element={<IntakePickerScreen />} />
              <Route path="intake/quick" element={<IntakeQuickScreen />} />
              <Route path="intake/convo" element={<IntakeConvoScreen />} />
              <Route path="intake/success" element={<IntakeSuccessScreen />} />

              <Route path="*" element={<Navigate to="/citizen/home" replace />} />
            </Routes>
          </IonContent>
        </IonPage>

        {/* Tab bar is a fixed sibling overlay (becomes IonTabBar in CI-3). */}
        {showTabBar && <TabBar />}
      </div>
    </div>
  );
}
